/**
 * TalentDetailPage — meta-cv detail layout for a single talent.
 *
 * Layout:
 *   1. TalentProfileHeader — full-width identity row with a back-arrow (to the pool)
 *      and a primary "Assign to job" CTA.
 *   2. Full-width inline tab bar (Profile | Activity) matching the Job Details tab style.
 *   3. Profile tab — balanced resume grid (Skills | Experience on row 1,
 *      Languages | Education on row 2) where paired cells share row height;
 *      Skills/Languages sit in bordered cards. Assigned Jobs is a dropdown in the tab bar.
 *   4. Activity tab — full-width activity timeline.
 *   5. Notes — full-width section at the BOTTOM, always visible (comments + composer).
 *
 * Section-level edit/add actions stay icon-only buttons with a tooltip/aria-label
 * ("a simple symbol describing what it does"); only the top-level Assign is a labelled CTA.
 *
 * Data: useTalent(id) with enabled: !!id guard.
 * Mutations: useAssignToJob, useAddComment, useUpdateSkills, useUpdateExperience, useUpdateEducation.
 *
 * All copy via i18next talentPool namespace. No hardcoded strings.
 * No gradients, no emojis — light brand only.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Briefcase,
  User,
  Activity as ActivityIcon,
  ChevronDown,
  Link2,
} from 'lucide-react';

import { useTalent } from '@/hooks/useTalentPool';
import { useTalentResumeDownload } from '@/hooks/useTalentResumeDownload';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';
import {
  useAssignToJob,
  useAddComment,
  useUpdateSkills,
  useUpdateExperience,
  useUpdateEducation,
  useDeleteTalent,
} from '@/hooks/useTalentMutations';

import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { TalentProfileHeader } from '@/components/talent-pool/detail/TalentProfileHeader';
import { ScoreGauge } from '@/components/talent-pool/detail/ScoreGauge';
import { ExperienceCard } from '@/components/talent-pool/detail/ExperienceCard';
import { EducationCard } from '@/components/talent-pool/detail/EducationCard';
import { TalentNotesSection } from '@/components/talent-pool/detail/TalentNotesSection';
import { ActivityItem } from '@/components/talent-pool/detail/ActivityItem';
import { AssignToJobModal } from '@/components/ui/organisms/AssignToJobModal';
import { Sheet } from '@/components/ui/organisms/Sheet/Sheet';

import { Button } from '@/components/ui/atoms/Button';
import { Tag } from '@/components/ui/atoms/Tag';
import { DropdownMenu } from '@/components/ui/organisms/DropdownMenu';
import { Skeleton } from '@/components/ui/atoms/Skeleton';
import { SkillChip } from '@/components/ui/atoms/SkillChip';
import { StatusPill } from '@/components/ui/atoms/StatusPill';
import { getCandidateStatusVariant } from '@/components/ui/atoms/StatusPill/statusPill.utils';
import { Typography } from '@/components/ui/atoms/Typography';
import { Tabs } from '@/components/ui/atoms/Tabs';
import { IllustratedEmptyState } from '@/components/ui/molecules/IllustratedEmptyState';

import type { TalentExperience, TalentEducation, TalentSkill } from '@/types/talent.types';
import { v4 as uuidv4 } from 'uuid';

// ── Tab config ──────────────────────────────────────────────────────────────────

const TAB_IDS = {
  profile: 'profile',
  activity: 'activity',
} as const;

type TabId = (typeof TAB_IDS)[keyof typeof TAB_IDS];

// ── Skeleton loading states ─────────────────────────────────────────────────────

const DetailHeaderSkeleton = () => (
  <div className="flex items-start gap-4 p-6 bg-light border-b border-light-lila/40">
    <Skeleton className="h-12 w-12 rounded-full shrink-0" />
    <div className="flex-1 flex flex-col gap-2">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-64" />
      <div className="flex gap-2 mt-1">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-14 rounded-full" />
      </div>
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-9 w-9 rounded-md" />
    </div>
  </div>
);

const SectionSkeleton = () => (
  <div className="flex flex-col gap-3 p-4">
    <Skeleton className="h-4 w-24" />
    <Skeleton.Card />
    <Skeleton.Card />
  </div>
);

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** "Mon YYYY – Mon YYYY" (or "… – <ongoing>") for the compact read-only lists. */
function compactRange(
  startDate: string | undefined,
  endDate: string | undefined,
  isOngoing: boolean | undefined,
  ongoingLabel: string,
): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const start = startDate ? fmt(startDate) : '';
  const end = isOngoing ? ongoingLabel : endDate ? fmt(endDate) : '';
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

/** Generate a stub new experience for "Add experience" flow */
function newExperience(): TalentExperience {
  return {
    id: uuidv4(),
    role: '',
    company: '',
    startDate: new Date().toISOString().slice(0, 10),
    isOngoing: true,
    displayOrder: 0,
  };
}

/** Generate a stub new education for "Add education" flow */
function newEducation(): TalentEducation {
  return {
    id: uuidv4(),
    institution: '',
    startDate: new Date().toISOString().slice(0, 10),
    isOngoing: true,
    displayOrder: 0,
  };
}

// ── Page component ──────────────────────────────────────────────────────────────

export const TalentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('talentPool');
  const stageLabel = useStageDisplayLabel();

  const { data: talent, isLoading, error } = useTalent(id);

  const assignMutation = useAssignToJob(id ?? '');
  const addCommentMutation = useAddComment(id ?? '');
  const updateSkillsMutation = useUpdateSkills(id ?? '');
  const updateExperienceMutation = useUpdateExperience(id ?? '');
  const updateEducationMutation = useUpdateEducation(id ?? '');
  const deleteMutation = useDeleteTalent(id ?? '');
  const { downloadResume, isDownloadingResume } = useTalentResumeDownload();

  const [activeTab, setActiveTab] = useState<TabId>(TAB_IDS.profile);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleConfirmDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setDeleteOpen(false);
        navigate('/talent-pool');
      },
    });
  };

  // Local skill edit state (in-memory before mutation)
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [localTechSkills, setLocalTechSkills] = useState<TalentSkill[]>([]);
  const [newSkillInput, setNewSkillInput] = useState('');
  const [addingSkillInline, setAddingSkillInline] = useState(false);

  // "Add" flows — draft items held in state so identity stays stable across re-renders
  const [draftExperience, setDraftExperience] = useState<TalentExperience | null>(null);
  const [draftEducation, setDraftEducation] = useState<TalentEducation | null>(null);

  // Edit side-sheets (experience / education edit lives in a sheet, keeping the
  // main view a compact read-only summary like the candidate Meta CV).
  const [editingExperience, setEditingExperience] = useState(false);
  const [editingEducation, setEditingEducation] = useState(false);

  // ── Event handlers ──────────────────────────────────────────────────────────

  const handleAssign = (jobId: string) => {
    assignMutation.mutate({ jobId });
    setAssignModalOpen(false);
  };

  const handleAddComment = (body: string) => {
    addCommentMutation.mutate({ body });
  };

  const handleStartEditSkills = () => {
    setLocalTechSkills(talent?.technicalSkills ?? []);
    setIsEditingSkills(true);
  };

  const handleSaveSkills = () => {
    updateSkillsMutation.mutate({
      technicalSkills: localTechSkills,
      softSkills: talent?.softSkills ?? [],
    });
    setIsEditingSkills(false);
  };

  const handleDiscardSkills = () => {
    setLocalTechSkills([]);
    setNewSkillInput('');
    setAddingSkillInline(false);
    setIsEditingSkills(false);
  };

  const handleRemoveSkill = (name: string) => {
    setLocalTechSkills((prev) => prev.filter((s) => s.name !== name));
  };

  const handleAddSkill = () => {
    const trimmed = newSkillInput.trim();
    if (!trimmed) return;
    setLocalTechSkills((prev) => [...prev, { name: trimmed }]);
    setNewSkillInput('');
  };

  const handleSaveExperience = (updated: TalentExperience) => {
    const current = talent?.experiences ?? [];
    const exists = current.find((e) => e.id === updated.id);
    const next = exists
      ? current.map((e) => (e.id === updated.id ? updated : e))
      : [updated, ...current];
    updateExperienceMutation.mutate({ experiences: next });
    setDraftExperience(null);
  };

  const handleCancelAddExperience = () => {
    setDraftExperience(null);
  };

  const handleDeleteExperience = (expId: string) => {
    const next = (talent?.experiences ?? []).filter((e) => e.id !== expId);
    updateExperienceMutation.mutate({ experiences: next });
  };

  const handleSaveEducation = (updated: TalentEducation) => {
    const current = talent?.education ?? [];
    const exists = current.find((e) => e.id === updated.id);
    const next = exists
      ? current.map((e) => (e.id === updated.id ? updated : e))
      : [updated, ...current];
    updateEducationMutation.mutate({ education: next });
    setDraftEducation(null);
  };

  const handleCancelAddEducation = () => {
    setDraftEducation(null);
  };

  const handleDeleteEducation = (eduId: string) => {
    const next = (talent?.education ?? []).filter((e) => e.id !== eduId);
    updateEducationMutation.mutate({ education: next });
  };

  // ── Loading state ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-background-gray">
        <DetailHeaderSkeleton />
        {/* Full-width 2-column resume skeleton (Skills/Languages | Experience/Education). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div className="flex flex-col gap-2">
            <SectionSkeleton />
            <SectionSkeleton />
          </div>
          <div className="flex flex-col gap-2">
            <SectionSkeleton />
            <SectionSkeleton />
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────────

  if (error || !talent) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background-gray">
        <IllustratedEmptyState
          icon={<Briefcase />}
          title={t('pool.emptyHeading')}
          description={t('pool.emptyBody')}
        />
      </div>
    );
  }

  // ── Profile tab — Skills section (editable chips) ─────────────────────────────

  const displaySkills = isEditingSkills ? localTechSkills : talent.technicalSkills;

  const skillsSection = (
    <section className="flex flex-col gap-3 h-full">
      <div className="flex items-center justify-between gap-2">
        <Typography variant="h4" weight="semibold" color="primary">
          {t('detail.sections.skills')}
        </Typography>
        {/* Discreet icon-only edit action — pencil + tooltip. */}
        {!isEditingSkills && (
          <Button
            type="icon"
            intent="muted"
            size="sm"
            icon={<Pencil/>}
            title={t('detail.iconTitles.editSkills')}
            aria-label={t('detail.iconTitles.editSkills')}
            onClick={handleStartEditSkills}
          />
        )}
      </div>

      {/* Bordered content card — title stays OUTSIDE for homogeneity with Experience/Education. */}
      <div className="flex-1 flex flex-col gap-3 rounded-lg border border-light-lila/40 bg-light p-4">
      {/* Chips + inline add pill (same pattern as job-creation skills). */}
      <div className="flex flex-wrap items-center gap-2">
        {displaySkills.map((skill) => (
          <SkillChip
            key={skill.name}
            label={skill.name}
            tone="info"
            closable={isEditingSkills}
            onRemove={() => handleRemoveSkill(skill.name)}
          />
        ))}

        {isEditingSkills &&
          (addingSkillInline ? (
            <span className="inline-flex items-center rounded-full bg-light-purple px-3 py-1">
              <input
                autoFocus
                type="text"
                value={newSkillInput}
                onChange={(e) => setNewSkillInput(e.target.value)}
                onBlur={() => {
                  handleAddSkill();
                  setAddingSkillInline(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSkill();
                  }
                  if (e.key === 'Escape') {
                    setNewSkillInput('');
                    setAddingSkillInline(false);
                  }
                }}
                placeholder={t('detail.addSkill')}
                className="w-28 bg-transparent text-xs font-semibold text-dark-invirtus placeholder-dark-invirtus/50 outline-none"
              />
            </span>
          ) : (
            <Button
              type="icon"
              intent="primary"
              size="sm"
              dashed
              onClick={() => setAddingSkillInline(true)}
              title={t('detail.iconTitles.addSkill')}
              aria-label={t('detail.iconTitles.addSkill')}
              icon={<Plus />}
            />
          ))}
      </div>

      {/* Save / discard only while editing. */}
      {isEditingSkills && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="outlined"
            intent="secondary"
            size="sm"
            label={t('detail.cancelEdit')}
            onClick={handleDiscardSkills}
          />
          <Button
            type="contained"
            intent="primary"
            size="sm"
            label={t('detail.saveSection')}
            onClick={handleSaveSkills}
            loading={updateSkillsMutation.isPending}
          />
        </div>
      )}
      </div>
    </section>
  );

  // ── Profile tab — Languages section (read-only, mirrors the Meta CV) ───────────
  // Hidden entirely when the talent has no languages.

  const languagesSection = (
    <section className="flex flex-col gap-3 h-full">
      {/* min-h-8 matches the 32px button-height header of the other sections so this
          card's top aligns with Formation/Education (whose header carries an icon button). */}
      <div className="flex items-center min-h-8">
        <Typography variant="h4" weight="semibold" color="primary">
          {t('detail.sections.languages')}
        </Typography>
      </div>
      {/* Bordered content card — title stays OUTSIDE for homogeneity with Experience/Education. */}
      <div className="flex-1 rounded-lg border border-light-lila/40 bg-light p-4">
        {talent.languages.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {talent.languages.map((language) => (
              <Tag key={language} intent="secondary" variant="subtle">
                {language}
              </Tag>
            ))}
          </div>
        ) : (
          <Typography variant="body-sm" color="muted">
            {t('detail.sections.languagesEmpty')}
          </Typography>
        )}
      </div>
    </section>
  );

  // Compact, read-only summary (like the candidate Meta CV) — one grouped card,
  // one line per role. Editing (add / edit / remove) happens in a side sheet.
  const experienceSection = (
    <section className="flex flex-col gap-3 h-full">
      <div className="flex items-center justify-between gap-2 min-h-8">
        <Typography variant="h4" weight="semibold" color="primary">
          {t('detail.sections.experience')}
        </Typography>
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Pencil/>}
          title={t('detail.editExperience')}
          aria-label={t('detail.editExperience')}
          onClick={() => setEditingExperience(true)}
        />
      </div>

      <div className="flex-1 rounded-lg border border-light-lila/40 bg-light p-4">
        {talent.experiences.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {talent.experiences.map((exp) => {
              const range = compactRange(
                exp.startDate, exp.endDate, exp.isOngoing, t('detail.exp.ongoing'),
              );
              return (
                <li key={exp.id} className="flex items-start gap-2">
                  <span
                    className="mt-2 h-1 w-1 shrink-0 rounded-full bg-dark-invirtus/40"
                    aria-hidden="true"
                  />
                  <Typography variant="body-sm" color="secondary">
                    <span className="font-medium text-dark-invirtus">{exp.role}</span>
                    {exp.company ? ` · ${exp.company}` : ''}
                    {range ? (
                      <span className="text-dark-invirtus/55">{` (${range})`}</span>
                    ) : null}
                  </Typography>
                </li>
              );
            })}
          </ul>
        ) : (
          <Typography variant="body-sm" color="muted">
            {t('detail.sections.experienceEmpty')}
          </Typography>
        )}
      </div>
    </section>
  );

  const educationSection = (
    <section className="flex flex-col gap-3 h-full">
      <div className="flex items-center justify-between gap-2 min-h-8">
        <Typography variant="h4" weight="semibold" color="primary">
          {t('detail.sections.education')}
        </Typography>
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Pencil/>}
          title={t('detail.editEducation')}
          aria-label={t('detail.editEducation')}
          onClick={() => setEditingEducation(true)}
        />
      </div>

      <div className="flex-1 rounded-lg border border-light-lila/40 bg-light p-4">
        {talent.education.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {talent.education.map((edu) => {
              const title = edu.degree || edu.fieldOfStudy || edu.institution;
              const showInstitution = edu.institution && (edu.degree || edu.fieldOfStudy);
              const range = compactRange(
                edu.startDate, edu.endDate, edu.isOngoing, t('detail.edu.ongoing'),
              );
              return (
                <li key={edu.id} className="flex items-start gap-2">
                  <span
                    className="mt-2 h-1 w-1 shrink-0 rounded-full bg-dark-invirtus/40"
                    aria-hidden="true"
                  />
                  <Typography variant="body-sm" color="secondary">
                    <span className="font-medium text-dark-invirtus">{title}</span>
                    {showInstitution ? ` · ${edu.institution}` : ''}
                    {range ? (
                      <span className="text-dark-invirtus/55">{` (${range})`}</span>
                    ) : null}
                  </Typography>
                </li>
              );
            })}
          </ul>
        ) : (
          <Typography variant="body-sm" color="muted">
            {t('detail.sections.educationEmpty')}
          </Typography>
        )}
      </div>
    </section>
  );

  // ── Notes section (always-visible, full width, page bottom) ───────────────────

  const notesSection = (
    <section className="flex flex-col gap-3">
      <Typography variant="h4" weight="semibold" color="primary">
        {t('section.notes')}
      </Typography>
      <div className="bg-light rounded-xl border border-light-lila/40 p-4">
        <TalentNotesSection
          comments={talent.comments}
          onAddComment={handleAddComment}
          isPending={addCommentMutation.isPending}
        />
      </div>
    </section>
  );

  // ── Activity tab content ──────────────────────────────────────────────────────

  const sortedEvents = [...talent.activityEvents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const activityContent = (
    <div className="bg-light rounded-xl border border-light-lila/40 p-4 flex flex-col">
      {sortedEvents.length === 0 && (
        <div className="py-6">
          <IllustratedEmptyState
            icon={<ActivityIcon />}
            title={t('activity.emptyHeading')}
            description={t('activity.emptyBody')}
          />
        </div>
      )}
      {sortedEvents.map((event) => (
        <ActivityItem key={event.id} event={event} />
      ))}
    </div>
  );

  // ── Assigned Jobs dropdown — sits in the tab bar as a trigger (not a content tab) ─
  // Lists the talent's assignments; each item shows the job title + its status pill
  // and navigates to that job (/job/:jobId). Empty state = a single disabled item.

  const assignmentCount = talent.assignments.length;

  const assignedJobsDropdown = (
    <DropdownMenu className="shrink-0">
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="shrink-0 inline-flex items-center gap-2 h-11 px-3.5 text-sm font-body transition-colors border-b-2 border-transparent text-dark-invirtus/60 hover:text-dark-invirtus hover:border-light-lila/60"
        >
          <Briefcase className="h-4 w-4" />
          <span>{t('section.assignedJobsCount', { count: assignmentCount })}</span>
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" className="min-w-96">
        {assignmentCount === 0 ? (
          <DropdownMenu.Item disabled>{t('section.assignedJobsEmpty')}</DropdownMenu.Item>
        ) : (
          talent.assignments.map((assignment) => (
            <DropdownMenu.Item
              key={assignment.jobId}
              onClick={() => navigate(`/job/${assignment.jobId}`)}
              className="!items-start gap-3 w-96 whitespace-normal"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
                <span className="truncate text-dark-invirtus font-medium">
                  {assignment.jobTitle}
                </span>
                {/* Stage pill + optional "Applied" badge (only for public-link applicants). */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {assignment.pipelineStageSemantic && assignment.pipelineStageLabel && (
                    <StatusPill
                      variant={getCandidateStatusVariant(assignment.pipelineStageSemantic)}
                      size="sm"
                    >
                      {stageLabel({
                        label: assignment.pipelineStageLabel,
                        stageType: assignment.pipelineStageSemantic,
                        isDefault: assignment.pipelineStageIsDefault ?? false,
                        isLabelCustomized: assignment.pipelineStageIsLabelCustomized ?? false,
                      })}
                    </StatusPill>
                  )}
                  {assignment.appliedViaLink && (
                    <StatusPill
                      variant="neutral"
                      size="sm"
                      icon={<Link2 aria-hidden="true" />}
                      className="bg-light border border-dark-invirtus/15"
                    >
                      {t('detail.assignedJob.appliedViaLink')}
                    </StatusPill>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <ScoreGauge
                  value={assignment.matchScore}
                  label={t('detail.assignedJob.match')}
                  tone="match"
                />
                <ScoreGauge
                  value={assignment.fitScore}
                  label={t('detail.assignedJob.fit')}
                  tone="fit"
                />
              </div>
            </DropdownMenu.Item>
          ))
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background-gray">
      {/* Identity header — full width, stays fixed while the body scrolls */}
      <div className="shrink-0">
        <TalentProfileHeader
          profile={talent}
          onAssignClick={() => setAssignModalOpen(true)}
          onBackClick={() => navigate('/talent-pool')}
          onDeleteClick={() => setDeleteOpen(true)}
          onDownloadResume={() => downloadResume(id ?? '', talent.fullName)}
          isDownloadingResume={isDownloadingResume}
        />
      </div>

      {/* Full-width inline tab bar — Assigned Jobs stays outside the tablist so its menu is not clipped. */}
      <div className="shrink-0 bg-light px-4 lg:px-6 flex items-center gap-1 border-b border-light-lila/40">
        <div className="min-w-0 overflow-x-auto">
          <Tabs
            aria-label={t('tabs.aria_label')}
            orientation="horizontal"
            variant="line"
            size="md"
            color="primary"
            value={activeTab}
            onValueChange={(id) => setActiveTab(id as TabId)}
            items={[
              {
                id: TAB_IDS.profile,
                label: t('tabs.profile'),
                icon: <User />,
              },
              {
                id: TAB_IDS.activity,
                label: t('tabs.activity'),
                icon: <ActivityIcon />,
              },
            ]}
          />
        </div>
        {/* Assigned Jobs — a dropdown trigger in the same bar (not a content tab). */}
        {assignedJobsDropdown}
      </div>

      {/* Scrollable body — only this region scrolls (header + tab bar stay put) */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-6 flex flex-col gap-8">
          {activeTab === TAB_IDS.profile && (
            <>
              {/* Balanced resume grid. Children flow row-by-row so paired cells share a
                 row height (items-stretch by default): Row 1 = Skills | Experience,
                 Row 2 = Languages | Education. Languages and Education sit in the same
                 grid row, so their boxes start at the same vertical line. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="min-w-0">{skillsSection}</div>
                <div className="min-w-0">{experienceSection}</div>
                <div className="min-w-0">{languagesSection}</div>
                <div className="min-w-0">{educationSection}</div>
              </div>
              {/* Notes also shown on the Profile tab (intentional duplication). */}
              {notesSection}
            </>
          )}

          {/* "Notes & Activity" tab — Notes first, then Activity, each titled. */}
          {activeTab === TAB_IDS.activity && (
            <>
              {notesSection}
              <section className="flex flex-col gap-3">
                <Typography variant="h4" weight="semibold" color="primary">
                  {t('section.activity')}
                </Typography>
                {activityContent}
              </section>
            </>
          )}
        </div>
      </div>

      {/* Assign to Job modal */}
      <AssignToJobModal
        open={assignModalOpen}
        talentId={id ?? ''}
        currentAssignments={talent.assignments}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssign}
        isPending={assignMutation.isPending}
      />

      {/* Experience edit — side sheet hosting the editable cards. */}
      <Sheet
        open={editingExperience}
        onOpenChange={(open) => {
          setEditingExperience(open);
          if (!open) handleCancelAddExperience();
        }}
        size="lg"
      >
        <Sheet.Header>
          <Sheet.Title>{t('detail.editExperience')}</Sheet.Title>
        </Sheet.Header>
        <Sheet.Body className="flex flex-col gap-3">
          <div>
            {!draftExperience && (
              <Button
                type="outlined"
                intent="secondary"
                size="sm"
                icon={<Plus/>}
                label={t('detail.addExperience')}
                className="w-full min-w-0"
                onClick={() => setDraftExperience(newExperience())}
              />
            )}
            {draftExperience && (
              <ExperienceCard
                key={draftExperience.id}
                experience={draftExperience}
                initialEditing
                onSave={handleSaveExperience}
                onDelete={handleCancelAddExperience}
              />
            )}
          </div>
          {talent.experiences.map((exp) => (
            <ExperienceCard
              key={exp.id}
              experience={exp}
              onSave={handleSaveExperience}
              onDelete={handleDeleteExperience}
            />
          ))}
        </Sheet.Body>
      </Sheet>

      {/* Education edit — side sheet hosting the editable cards. */}
      <Sheet
        open={editingEducation}
        onOpenChange={(open) => {
          setEditingEducation(open);
          if (!open) handleCancelAddEducation();
        }}
        size="lg"
      >
        <Sheet.Header>
          <Sheet.Title>{t('detail.editEducation')}</Sheet.Title>
        </Sheet.Header>
        <Sheet.Body className="flex flex-col gap-3">
          <div className="sticky top-0 z-[1] shrink-0 min-w-0 bg-light">
            {!draftEducation && (
              <Button
                type="outlined"
                intent="secondary"
                size="sm"
                icon={<Plus/>}
                label={t('detail.addEducation')}
                className="w-full min-w-0"
                onClick={() => setDraftEducation(newEducation())}
              />
            )}
            {draftEducation && (
              <EducationCard
                key={draftEducation.id}
                education={draftEducation}
                initialEditing
                onSave={handleSaveEducation}
                onDelete={handleCancelAddEducation}
              />
            )}
          </div>
          {talent.education.map((edu) => (
            <EducationCard
              key={edu.id}
              education={edu}
              onSave={handleSaveEducation}
              onDelete={handleDeleteEducation}
            />
          ))}
        </Sheet.Body>
      </Sheet>

      {/* Delete confirmation — destructive, cascades to this tenant's candidacies. */}
      <ConfirmationDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('detail.deleteConfirmTitle')}
        message={t('detail.deleteConfirmBody', { name: talent.fullName })}
        buttons={[
          {
            label: t('detail.deleteCancel'),
            onClick: () => setDeleteOpen(false),
            type: 'outlined',
            intent: 'secondary',
            disabled: deleteMutation.isPending,
          },
          {
            label: t('detail.deleteConfirmAction'),
            onClick: handleConfirmDelete,
            type: 'contained',
            intent: 'danger',
            loading: deleteMutation.isPending,
            disabled: deleteMutation.isPending,
          },
        ]}
      />
    </div>
  );
};

export default TalentDetailPage;
